import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
  IconButton,
  Separator,
  Pivot,
  PivotItem
} from '@fluentui/react';
import {
  EditRegular,
  DeleteRegular,
  AddRegular,
  SaveRegular,
  DismissRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './HelpEditor.module.css';

// Bilingual text structure
interface BilingualText {
  en: string;
  he: string;
}

// Help Feature structure (from Cosmos help_features document)
interface HelpFeature {
  id: string;
  category: 'source' | 'feature';
  title: BilingualText;
  description: BilingualText;
  icon: string;
  colorScheme: string;
  triggerWords: { en: string[]; he: string[] };
  examples: Array<{
    query: BilingualText;
    explanation: BilingualText;
    expectedSource: string;
  }>;
  commonMistakes: Array<{
    wrong: BilingualText;
    right: BilingualText;
    explanation: BilingualText;
  }>;
  tips: BilingualText[];
  relatedFeatures: string[];
  popularity: number;
  lastUpdated: string;
}

interface HelpEditorProps {
  isVisible: boolean;
  onDismiss: () => void;
}

// Helper to get text from bilingual field
const getText = (field: BilingualText | string | undefined, lang: 'en' | 'he' = 'en'): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field[lang] || '';
};

// Helper to create bilingual text
const createBilingual = (en: string = '', he: string = ''): BilingualText => ({ en, he });

// Helper to normalize a feature - ensures all arrays exist
const normalizeFeature = (feature: Partial<HelpFeature>): HelpFeature => ({
  id: feature.id || `feature_${Date.now()}`,
  category: feature.category || 'feature',
  title: feature.title || createBilingual('', ''),
  description: feature.description || createBilingual('', ''),
  icon: feature.icon || '✨',
  colorScheme: feature.colorScheme || 'modelKnowledge',
  triggerWords: feature.triggerWords || { en: [], he: [] },
  examples: feature.examples || [],
  commonMistakes: feature.commonMistakes || [],
  tips: feature.tips || [],
  relatedFeatures: feature.relatedFeatures || [],
  popularity: feature.popularity ?? 50,
  lastUpdated: feature.lastUpdated || new Date().toISOString().split('T')[0]
});

const HelpEditor: React.FC<HelpEditorProps> = ({ isVisible, onDismiss }) => {
  const [features, setFeatures] = useState<HelpFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: MessageBarType; text: string } | null>(null);
  const [editingFeature, setEditingFeature] = useState<HelpFeature | null>(null);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState<string | null>(null);
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [editLang, setEditLang] = useState<'en' | 'he'>('en');

  useEffect(() => {
    if (isVisible) {
      loadFeatures();
    }
  }, [isVisible]);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/help/features', {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        // Normalize all features to ensure required arrays exist
        const normalizedFeatures = (data.features || []).map(normalizeFeature);
        setFeatures(normalizedFeatures);
      } else {
        setMessage({ type: MessageBarType.error, text: 'Failed to load help features' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error loading help features' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditFeature = (feature: HelpFeature) => {
    // Deep clone and normalize to ensure all arrays exist
    const cloned = JSON.parse(JSON.stringify(feature));
    setEditingFeature(normalizeFeature(cloned));
    setIsEditPanelOpen(true);
  };

  const handleAddFeature = () => {
    const newFeature: HelpFeature = {
      id: `feature_${Date.now()}`,
      category: 'feature',
      title: createBilingual('New Feature', 'פיצ\'ר חדש'),
      description: createBilingual('', ''),
      icon: '✨',
      colorScheme: 'modelKnowledge',
      triggerWords: { en: [], he: [] },
      examples: [],
      commonMistakes: [],
      tips: [],
      relatedFeatures: [],
      popularity: 50,
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    setEditingFeature(newFeature);
    setIsEditPanelOpen(true);
  };

  const handleSaveFeature = async () => {
    if (!editingFeature) return;

    try {
      setSaving(true);
      const isNew = !features.find(f => f.id === editingFeature.id);
      
      // Update the features array with the edited/new feature
      const updatedFeature = {
        ...editingFeature,
        lastUpdated: new Date().toISOString().split('T')[0]
      };
      
      let updatedFeatures: HelpFeature[];
      if (isNew) {
        updatedFeatures = [...features, updatedFeature];
      } else {
        updatedFeatures = features.map(f => f.id === editingFeature.id ? updatedFeature : f);
      }

      // Save the entire features array to the features endpoint
      const response = await authenticatedFetch('/api/help/features', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          features: updatedFeatures,
          categories: {},
          quickStartSteps: [],
          metadata: {
            version: '1.0.0',
            lastUpdated: new Date().toISOString(),
            languages: ['en', 'he']
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: MessageBarType.success, text: `Feature ${isNew ? 'added' : 'updated'} successfully` });
        setIsEditPanelOpen(false);
        setEditingFeature(null);
        loadFeatures();
      } else {
        setMessage({ type: MessageBarType.error, text: data.error || 'Failed to save feature' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error saving feature' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeature = async (featureId: string) => {
    try {
      // Remove the feature from the array and save the entire array
      const updatedFeatures = features.filter(f => f.id !== featureId);
      
      const response = await authenticatedFetch('/api/help/features', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          features: updatedFeatures,
          categories: {},
          quickStartSteps: [],
          metadata: {
            version: '1.0.0',
            lastUpdated: new Date().toISOString(),
            languages: ['en', 'he']
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: MessageBarType.success, text: 'Feature deleted successfully' });
        loadFeatures();
      } else {
        setMessage({ type: MessageBarType.error, text: data.error || 'Failed to delete feature' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error deleting feature' });
    } finally {
      setDeleteDialogVisible(false);
      setFeatureToDelete(null);
    }
  };

  const confirmDelete = (featureId: string) => {
    setFeatureToDelete(featureId);
    setDeleteDialogVisible(true);
  };

  const handleResetToDefaults = async () => {
    try {
      setResetting(true);
      const response = await authenticatedFetch('/api/help/features/reset', {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: MessageBarType.success, text: 'Help features reset to defaults' });
        loadFeatures();
      } else {
        setMessage({ type: MessageBarType.error, text: data.error || 'Failed to reset' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error resetting' });
    } finally {
      setResetting(false);
      setResetDialogVisible(false);
    }
  };

  // Update bilingual field
  const updateBilingualField = (fieldPath: string, lang: 'en' | 'he', value: string) => {
    if (!editingFeature) return;
    
    const updated = { ...editingFeature };
    const keys = fieldPath.split('.');
    let obj: any = updated;
    
    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]];
    }
    
    const lastKey = keys[keys.length - 1];
    if (typeof obj[lastKey] === 'object' && obj[lastKey] !== null) {
      obj[lastKey] = { ...obj[lastKey], [lang]: value };
    } else {
      obj[lastKey] = { en: lang === 'en' ? value : '', he: lang === 'he' ? value : '' };
    }
    
    setEditingFeature(updated);
  };

  // Update simple field
  const updateField = (field: string, value: any) => {
    if (!editingFeature) return;
    setEditingFeature({ ...editingFeature, [field]: value });
  };

  // Update trigger words
  const updateTriggerWords = (lang: 'en' | 'he', value: string) => {
    if (!editingFeature) return;
    const words = value.split(',').map(w => w.trim()).filter(Boolean);
    setEditingFeature({
      ...editingFeature,
      triggerWords: { ...editingFeature.triggerWords, [lang]: words }
    });
  };

  // Add example
  const addExample = () => {
    if (!editingFeature) return;
    setEditingFeature({
      ...editingFeature,
      examples: [
        ...editingFeature.examples,
        {
          query: createBilingual(),
          explanation: createBilingual(),
          expectedSource: editingFeature.title.en || 'Feature'
        }
      ]
    });
  };

  // Update example
  const updateExample = (index: number, field: string, lang: 'en' | 'he', value: string) => {
    if (!editingFeature) return;
    const examples = [...editingFeature.examples];
    if (field === 'expectedSource') {
      examples[index] = { ...examples[index], expectedSource: value };
    } else {
      examples[index] = {
        ...examples[index],
        [field]: { ...(examples[index] as any)[field], [lang]: value }
      };
    }
    setEditingFeature({ ...editingFeature, examples });
  };

  // Remove example
  const removeExample = (index: number) => {
    if (!editingFeature) return;
    const examples = [...editingFeature.examples];
    examples.splice(index, 1);
    setEditingFeature({ ...editingFeature, examples });
  };

  // Add tip
  const addTip = () => {
    if (!editingFeature) return;
    setEditingFeature({
      ...editingFeature,
      tips: [...editingFeature.tips, createBilingual()]
    });
  };

  // Update tip
  const updateTip = (index: number, lang: 'en' | 'he', value: string) => {
    if (!editingFeature) return;
    const tips = [...editingFeature.tips];
    tips[index] = { ...tips[index], [lang]: value };
    setEditingFeature({ ...editingFeature, tips });
  };

  // Remove tip
  const removeTip = (index: number) => {
    if (!editingFeature) return;
    const tips = [...editingFeature.tips];
    tips.splice(index, 1);
    setEditingFeature({ ...editingFeature, tips });
  };

  const columns: IColumn[] = [
    {
      key: 'icon',
      name: '',
      minWidth: 40,
      maxWidth: 40,
      onRender: (item: HelpFeature) => <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
    },
    {
      key: 'title',
      name: 'Title',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: HelpFeature) => <Text>{getText(item.title)}</Text>
    },
    {
      key: 'category',
      name: 'Category',
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: HelpFeature) => (
        <Text style={{ textTransform: 'capitalize' }}>{item.category}</Text>
      )
    },
    {
      key: 'description',
      name: 'Description',
      minWidth: 200,
      maxWidth: 350,
      isResizable: true,
      onRender: (item: HelpFeature) => (
        <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getText(item.description)}
        </Text>
      )
    },
    {
      key: 'triggers',
      name: 'Trigger Words',
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: HelpFeature) => (
        <Text>{item.triggerWords?.en?.length || 0} EN, {item.triggerWords?.he?.length || 0} HE</Text>
      )
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: HelpFeature) => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title="Edit feature"
            onClick={() => handleEditFeature(item)}
          />
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Delete feature"
            onClick={() => confirmDelete(item.id)}
          />
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Panel
        headerText="User Help Features Editor"
        isOpen={isVisible}
        onDismiss={onDismiss}
        type={PanelType.extraLarge}
        className={styles.helpEditorPanel}
      >
        <Stack tokens={{ childrenGap: 20 }}>
          {message && (
            <MessageBar
              messageBarType={message.type}
              onDismiss={() => setMessage(null)}
            >
              {message.text}
            </MessageBar>
          )}

          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="xLarge">Manage Help Features ({features.length} items)</Text>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <DefaultButton
                text="Reset to Defaults"
                iconProps={{ iconName: 'Refresh' }}
                onClick={() => setResetDialogVisible(true)}
                disabled={resetting}
              />
              <PrimaryButton
                text="Add Feature"
                iconProps={{ iconName: 'Add' }}
                onClick={handleAddFeature}
              />
            </Stack>
          </Stack>

          {loading ? (
            <Stack horizontalAlign="center" tokens={{ childrenGap: 10 }}>
              <Spinner size={SpinnerSize.large} />
              <Text>Loading help features...</Text>
            </Stack>
          ) : (
            <DetailsList
              items={features}
              columns={columns}
              layoutMode={DetailsListLayoutMode.justified}
              selectionMode={SelectionMode.none}
              className={styles.sectionsList}
            />
          )}
        </Stack>
      </Panel>

      {/* Edit Feature Panel */}
      <Panel
        headerText={editingFeature && features.find(f => f.id === editingFeature.id) 
          ? `Edit: ${getText(editingFeature.title)}` 
          : "Add New Feature"}
        isOpen={isEditPanelOpen}
        onDismiss={() => {
          setIsEditPanelOpen(false);
          setEditingFeature(null);
        }}
        type={PanelType.large}
        className={styles.editPanel}
      >
        {editingFeature && (
          <Stack tokens={{ childrenGap: 16 }}>
            {/* Language Toggle */}
            <Pivot
              selectedKey={editLang}
              onLinkClick={(item) => setEditLang(item?.props.itemKey as 'en' | 'he' || 'en')}
            >
              <PivotItem headerText="🇬🇧 English" itemKey="en" />
              <PivotItem headerText="🇮🇱 Hebrew" itemKey="he" />
            </Pivot>

            {/* Basic Info */}
            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <Stack.Item grow={3}>
                <TextField
                  label={`Title (${editLang.toUpperCase()})`}
                  value={getText(editingFeature.title, editLang)}
                  onChange={(_, v) => updateBilingualField('title', editLang, v || '')}
                  required
                  dir={editLang === 'he' ? 'rtl' : 'ltr'}
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <TextField
                  label="Icon (Emoji)"
                  value={editingFeature.icon}
                  onChange={(_, v) => updateField('icon', v || '')}
                  styles={{ root: { width: 80 } }}
                />
              </Stack.Item>
            </Stack>

            <TextField
              label={`Description (${editLang.toUpperCase()})`}
              value={getText(editingFeature.description, editLang)}
              onChange={(_, v) => updateBilingualField('description', editLang, v || '')}
              multiline
              rows={2}
              dir={editLang === 'he' ? 'rtl' : 'ltr'}
            />

            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <Stack.Item grow={1}>
                <TextField
                  label="Category"
                  value={editingFeature.category}
                  onChange={(_, v) => updateField('category', v as 'source' | 'feature' || 'feature')}
                  description="source or feature"
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <TextField
                  label="Color Scheme"
                  value={editingFeature.colorScheme}
                  onChange={(_, v) => updateField('colorScheme', v || '')}
                  description="e.g., office365, liveSearch, deepResearch"
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <TextField
                  label="Popularity"
                  type="number"
                  value={String(editingFeature.popularity)}
                  onChange={(_, v) => updateField('popularity', parseInt(v || '50', 10))}
                />
              </Stack.Item>
            </Stack>

            <Separator />

            {/* Trigger Words */}
            <Text variant="large">🎯 Trigger Words</Text>
            <TextField
              label={`Trigger Words (${editLang.toUpperCase()}) - comma separated`}
              value={editingFeature.triggerWords?.[editLang]?.join(', ') || ''}
              onChange={(_, v) => updateTriggerWords(editLang, v || '')}
              dir={editLang === 'he' ? 'rtl' : 'ltr'}
              description="Words that activate this feature when detected in user queries"
            />

            <Separator />

            {/* Examples */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="large">💬 Examples ({editingFeature.examples?.length || 0})</Text>
              <DefaultButton text="Add Example" iconProps={{ iconName: 'Add' }} onClick={addExample} />
            </Stack>

            {(editingFeature.examples || []).map((example, idx) => (
              <Stack key={idx} tokens={{ childrenGap: 8 }} className={styles.contentSection}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                  <Text variant="medium">Example {idx + 1}</Text>
                  <IconButton iconProps={{ iconName: 'Delete' }} onClick={() => removeExample(idx)} />
                </Stack>
                <TextField
                  label={`Query (${editLang.toUpperCase()})`}
                  value={getText(example.query, editLang)}
                  onChange={(_, v) => updateExample(idx, 'query', editLang, v || '')}
                  dir={editLang === 'he' ? 'rtl' : 'ltr'}
                  multiline
                  rows={2}
                />
                <TextField
                  label={`Explanation (${editLang.toUpperCase()})`}
                  value={getText(example.explanation, editLang)}
                  onChange={(_, v) => updateExample(idx, 'explanation', editLang, v || '')}
                  dir={editLang === 'he' ? 'rtl' : 'ltr'}
                />
              </Stack>
            ))}

            <Separator />

            {/* Tips */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="large">💡 Tips ({editingFeature.tips?.length || 0})</Text>
              <DefaultButton text="Add Tip" iconProps={{ iconName: 'Add' }} onClick={addTip} />
            </Stack>

            {(editingFeature.tips || []).map((tip, idx) => (
              <Stack key={idx} horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                <Stack.Item grow>
                  <TextField
                    label={`Tip ${idx + 1} (${editLang.toUpperCase()})`}
                    value={getText(tip, editLang)}
                    onChange={(_, v) => updateTip(idx, editLang, v || '')}
                    dir={editLang === 'he' ? 'rtl' : 'ltr'}
                  />
                </Stack.Item>
                <IconButton iconProps={{ iconName: 'Delete' }} onClick={() => removeTip(idx)} />
              </Stack>
            ))}

            <Separator />

            {/* Save/Cancel */}
            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
              <DefaultButton
                text="Cancel"
                onClick={() => {
                  setIsEditPanelOpen(false);
                  setEditingFeature(null);
                }}
              />
              <PrimaryButton
                text="Save Feature"
                iconProps={{ iconName: 'Save' }}
                onClick={handleSaveFeature}
                disabled={saving || !getText(editingFeature.title)}
              />
            </Stack>

            {saving && (
              <Stack horizontalAlign="center">
                <Spinner size={SpinnerSize.medium} />
              </Stack>
            )}
          </Stack>
        )}
      </Panel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!deleteDialogVisible}
        onDismiss={() => setDeleteDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Confirm Delete',
          subText: 'Are you sure you want to delete this help feature? This action cannot be undone.',
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text="Delete"
            onClick={() => featureToDelete && handleDeleteFeature(featureToDelete)}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => setDeleteDialogVisible(false)}
          />
        </DialogFooter>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        hidden={!resetDialogVisible}
        onDismiss={() => setResetDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Reset to Defaults',
          subText: 'This will replace all help features with the defaults from helpFeatures.json. Continue?',
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text={resetting ? "Resetting..." : "Reset"}
            onClick={handleResetToDefaults}
            disabled={resetting}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => setResetDialogVisible(false)}
            disabled={resetting}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default HelpEditor;
